/**
 * Analysis API client (server-backed)
 */

import { apiClient, API_BASE_URL } from "./apiClient";

export const analysisAPI = {
  /**
   * List analyses. If orgId is provided, lists for that org; otherwise lists all for current user.
   * Returns { list, counts } when backend sends counts.
   */
  listAnalyses: async (orgId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.model_category) params.append("model_category", filters.model_category);
    if (filters.status) params.append("status", filters.status);
    if (filters.client || filters.client_id) params.append("client", filters.client || filters.client_id);
    if (filters.search) params.append("search", filters.search);

    const url = orgId
      ? `/analysis/org/${orgId}/?${params.toString()}`
      : `/analysis/?${params.toString()}`;
    const payload = await apiClient.get(url);

    const list = Array.isArray(payload) ? payload : payload?.data ?? payload?.results ?? [];
    const counts = payload?.counts ?? null;
    return counts != null ? { list, counts } : list;
  },

  getAnalysis: (analysisId) => apiClient.get(`/analysis/${analysisId}/`),

  createAnalysis: (orgId, data) =>
    apiClient.post(`/analysis/org/${orgId}/run/`, data),

  updateAnalysis: (analysisId, data) =>
    apiClient.patch(`/analysis/${analysisId}/`, data),

  executeAnalysis: (analysisId) =>
    apiClient.post(`/analysis/${analysisId}/execute/`),

  deleteAnalysis: (analysisId) => apiClient.delete(`/analysis/${analysisId}/`),

  getModelCategories: () => apiClient.get("/analysis/model_categories/"),

  getModels: (category) =>
    apiClient.get(`/analysis/models/${encodeURIComponent(category)}/`),

  /** Run a prediction using a persisted model for a given analysis. */
  predict: (analysisId, input) =>
    apiClient.post(`/analysis/${analysisId}/predict/`, { input }),

  /** Get prediction log history for an analysis. */
  predictionHistory: (analysisId) =>
    apiClient.get(`/analysis/${analysisId}/predict/history/`),

  /** Organisation-level intelligence summary. */
  getOrganisationIntelligence: async () => {
    const raw = await apiClient.get("/analysis/organisation-intelligence/");
    // Normalise: backend may return flat object or wrapped { data: ... }
    if (raw && typeof raw === "object") {
      // If it has health_score it's already the right shape
      if ("health_score" in raw) return raw;
      // Otherwise try to unwrap data
      if (raw.data && "health_score" in raw.data) return raw.data;
    }
    return raw;
  },

  /** Export analysis results. format: 'csv' | 'pdf'. Triggers a browser download. */
  exportAnalysis: async (analysisId, format = "csv") => {
    // Build the full URL manually so we can use raw fetch with the correct Accept header
    const token = localStorage.getItem("access_token");
    const csrf = (() => {
      const m = document.cookie.match(/\bcsrftoken=([^;]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    })();

    const base = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const url = `${base}/analysis/${analysisId}/export/?format=${format}`;

    const headers = { Accept: format === "pdf" ? "application/pdf" : "text/csv" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (csrf) headers["X-CSRFToken"] = csrf;

    const res = await fetch(url, { headers, credentials: "include" });

    if (!res.ok) {
      let msg = "Export failed";
      try {
        const body = await res.json();
        msg = body?.error || body?.message || msg;
      } catch (_) {}
      throw new Error(msg);
    }

    const blob = await res.blob();
    const filename = `analysis_${analysisId}_results.${format}`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
    return filename;
  },
};
